const preferredRegions = ['europe', 'us'];

/**
 * Analytics Filter Builder Types
 *
 * Supports dynamic filter rules with property, operator, and value(s).
 * Rules are combined with AND logic.
 */
/**
 * Available filter fields based on WebsiteEvent properties
 */
const AnalyticsFilterFields = [
    'campaignId',
    'variantId',
    'websiteId',
    'device',
    'os',
    'browser',
    'country',
    'city',
    'adNetworkProvider',
    'referrerDomain',
    'origin',
    'search',
    'utmSource',
    'utmMedium',
    'isBot',
    'botName',
    'environment',
];
/**
 * Field metadata configuration
 */
const AnalyticsFilterFieldsMeta = [
    {
        field: 'campaignId',
        label: 'Dynamic Page',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side campaigns
    },
    {
        field: 'variantId',
        label: 'Variant',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side variants
    },
    {
        field: 'websiteId',
        label: 'Experiment Variation',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side websites with experiments
    },
    {
        field: 'device',
        label: 'Device',
        type: 'enum',
        staticOptions: [
            { value: 'mobile', label: 'Mobile' },
            { value: 'desktop', label: 'Desktop' },
            { value: 'tablet', label: 'Tablet' },
        ],
    },
    {
        field: 'os',
        label: 'Operating System',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'browser',
        label: 'Browser',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'country',
        label: 'Country',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'city',
        label: 'City',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adNetworkProvider',
        label: 'Ad Network',
        type: 'enum',
        staticOptions: [
            { value: 'google', label: 'Google' },
            { value: 'facebook', label: 'Facebook' },
            { value: 'microsoft', label: 'Microsoft' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'pinterest', label: 'Pinterest' },
            { value: 'twitter', label: 'Twitter' },
            { value: 'snapchat', label: 'Snapchat' },
            { value: 'taboola', label: 'Taboola' },
            { value: 'outbrain', label: 'Outbrain' },
            { value: 'reddit', label: 'Reddit' },
            { value: 'unknown', label: 'Unknown' },
        ],
    },
    {
        field: 'referrerDomain',
        label: 'Referrer Domain',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'origin',
        label: 'Origin',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'search',
        label: 'URL Params',
        type: 'string',
        dynamicOptions: false,
    },
    {
        field: 'utmSource',
        label: 'UTM Source',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'utmMedium',
        label: 'UTM Medium',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'isBot',
        label: 'Is Bot',
        type: 'boolean',
        staticOptions: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
        ],
    },
    {
        field: 'botName',
        label: 'Bot Name',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'environment',
        label: 'Environment',
        type: 'enum',
        staticOptions: [
            { value: 'production', label: 'Production' },
            { value: 'test', label: 'Test' },
        ],
    },
];
/**
 * Filter operators
 */
const AnalyticsFilterOperators = [
    'is',
    'isNot',
    'isAnyOf',
    'isNotAnyOf',
    'contains',
    'doesNotContain',
    'startsWith',
    'isEmpty',
    'isNotEmpty',
];
/**
 * Operators available for each field type
 */
const OperatorsByFieldType = {
    string: [
        'isAnyOf',
        'isNotAnyOf',
        'contains',
        'doesNotContain',
        'startsWith',
        'isEmpty',
        'isNotEmpty',
    ],
    select: ['isAnyOf', 'isNotAnyOf'],
    boolean: ['is', 'isNot'],
    enum: ['isAnyOf', 'isNotAnyOf'],
};
/**
 * Operator display labels
 */
const OperatorLabels = {
    is: 'equals',
    isNot: 'not equals',
    isAnyOf: 'is any of',
    isNotAnyOf: 'is not any of',
    contains: 'contains',
    doesNotContain: 'does not contain',
    startsWith: 'starts with',
    isEmpty: 'is empty',
    isNotEmpty: 'is not empty',
};
/**
 * Operators that don't require a value
 */
const NoValueOperators = [
    'isEmpty',
    'isNotEmpty',
];
/**
 * Operators that accept multiple values
 */
const MultiValueOperators = [
    'isAnyOf',
    'isNotAnyOf',
];
/**
 * Operators that require free-text input (no dropdown options)
 */
const TextOperators = [
    'is',
    'isNot',
    'contains',
    'doesNotContain',
    'startsWith',
];
/**
 * Empty/default filter state
 */
const EmptyAnalyticsFilters = {
    logic: 'and',
    rules: [],
};

const deviceConstraints = ['desktop', 'mobile', 'tablet'];

const customCodeLocations = ['head', 'body'];
/**
 * Check if a CustomCode uses the legacy format (code + location)
 */
const isLegacyCustomCode = (customCode) => {
    return (customCode.code !== undefined &&
        customCode.location !== undefined &&
        customCode.headCode === undefined &&
        customCode.bodyCode === undefined);
};

const dynamicPageGoals = [
    'leads',
    'appDownload',
    'linkClick',
    'calendly',
];

/** Types of metrics that can be tracked in experiments */
const experimentMetricTypes = [
    'default', // Built-in SuperFunnel metrics
    'externalPixel', // External pixel events (GTM, etc.)
    'customEvent', // Custom analytics events defined in account
];
/** Default built-in metric IDs */
const defaultExperimentMetricIds = [
    'conversionRate',
    'visitors',
    'conversions',
    'buttonClicks',
    'formSubmissions',
    'uniqueLinkClicks',
];
const experimentEndModes = [
    'manual',
    'statisticalSignificance',
    'duration',
    'visitors',
];
const experimentStatuses = [
    'running',
    'completed',
    'cancelled',
];
// ============================================================================
// Experiment Statistics Types
// ============================================================================
/** Status of the experiment decision process */
const experimentStatsStatuses = [
    'collecting_data', // Not enough data yet
    'inconclusive', // Enough data but no clear winner
    'winner_found', // Clear winner identified
];

const formFieldTypes = [
    'text',
    'email',
    'businessEmail',
    'tel',
    'textArea',
    'number',
    'select',
    'multiChoice',
    'pictureChoice',
    'country',
    'state',
    'date',
    'consent',
    'hidden',
    'address',
];

// ============================================================================
// HTML Stepper Field Types
// These represent fields extracted from HTML data-sf-* attributes
// ============================================================================
const htmlStepperFieldTypes = [
    'text',
    'email',
    'tel',
    'number',
    'textarea',
    'date',
    'select',
    'multiChoice',
    'checkbox',
    'range',
    'country',
    'state',
    'phone',
    'address',
    'hidden',
];

/**
 * Type guard to check if tokens are OAuth tokens (have refresh capability)
 */
function isOAuthTokens(tokens) {
    return 'refreshToken' in tokens && 'expiresAt' in tokens;
}

const headerSectionVariants = ['v1'];
const bannerSectionVariants = ['v1'];
const comparisunHeroSectionVariants = [
    'comparisunV1',
    'comparisunV2',
];
const leadsLiveHeroSectionVariants = ['leadsLiveV1'];
const betterspeechHeroSectionVariants = [
    'betterspeechV1',
    'betterspeechV2',
];
const firstConnectHeroSectionVariants = ['fcV1'];
const z2aHeroSectionVariants = ['z2aV1', 'z2aV2'];
const harelHeroSectionVariants = ['harelV1', 'harelV2', 'harelV3'];
const heroSectionVariants = [
    'v1',
    'v2',
    'v3',
    'v4',
    'v5',
    'v6',
    'v7',
    'v8',
    'v9',
    'v10',
    'v11',
    'v12',
];
const ctaSectionVariants = ['v1', 'v2', 'v3', 'v4'];
const betterspeechCTASectionVariants = [
    'betterspeechV1',
    'betterspeechV2',
];
const logosSectionVariants = [
    'v1',
    'v2',
    'v3',
    'v4',
    'v5',
    'v6',
];
const z2aLogosSectionVariants = ['z2aV1'];
const trustpilotSectionVariants = ['v1'];
const featuresSectionVariants = ['v1', 'v2', 'v3', 'v4'];
const z2aFeaturesSectionVariants = ['z2aV1'];
const textImageSectionVariants = ['v1', 'v2', 'v3', 'v4'];
const textSectionVariants = ['v1'];
const comparisunTextSectionVariants = ['comparisunV1'];
const testimonialsSectionVariants = [
    'v1',
    'v2',
    'v3',
    'v4',
    'v5',
    'v6',
];
const pressMentionsSectionVariants = ['v1'];
const comparisunComparisonTopItemsSectionVariants = [
    'comparisunV1',
    'comparisunV2',
    'comparisunV3',
    'comparisunV4',
];
const comparisonTopItemsSectionVariants = ['v1'];
const comparisunComparisonListSectionVariants = [
    'comparisunV1',
    'comparisunV2',
    'comparisunV3',
    'comparisunV4',
    'comparisunV5',
    'comparisunV6',
    'comparisunV7',
];
const leadsLiveComparisonListSectionVariants = ['leadsLiveV1'];
const z2aComparisonListSectionVariants = ['z2aV1', 'z2aV2'];
const comparisonListSectionVariants = ['v1'];
const categoriesSectionVariants = ['v1', 'v2'];
const betterspeechShowcaseSectionVariants = ['betterspeechV1'];
const z2aShowcaseSectionVariants = ['z2aV1'];
const showcaseSectionVariants = ['v1', 'v2', 'v3'];
const calendlySectionVariants = ['v1'];
const formSectionVariants = ['v1', 'v2'];
const faqsSectionVariants = ['v1', 'v2', 'v3', 'v4', 'v5'];
const comparisunFaqsSectionVariants = ['comparisunV1'];
const z2aFaqsSectionVariants = ['z2aV1'];
const customCodeSectionVariants = ['v1'];
const customHTMLSectionVariants = ['v1'];
const footerSectionVariants = ['v1'];
const comparisunFooterSectionVariants = ['comparisunV1'];
const topComparedFooterSectionVariants = ['topComparedV1'];
const z2aFooterSectionVariants = ['z2aV1'];
const sectionTypeToVariantMapping = {
    header: headerSectionVariants,
    banner: bannerSectionVariants,
    features: featuresSectionVariants,
    textImage: textImageSectionVariants,
    text: textSectionVariants,
    testimonials: testimonialsSectionVariants,
    form: formSectionVariants,
    faqs: faqsSectionVariants,
    footer: footerSectionVariants,
    hero: heroSectionVariants,
    cta: ctaSectionVariants,
    calendly: calendlySectionVariants,
    logos: logosSectionVariants,
    code: customCodeSectionVariants,
    html: customHTMLSectionVariants,
    categories: categoriesSectionVariants,
    showcase: showcaseSectionVariants,
    pressMentions: pressMentionsSectionVariants,
    comparisonList: comparisonListSectionVariants,
    comparisonTopItems: comparisonTopItemsSectionVariants,
    trustpilot: trustpilotSectionVariants,
    quinstreetList: ['v1'],
};
const accountSectionTypeToVariantMapping = {
    // Comparisun
    'deDjFxdKX7qLdBALhQ2B': {
        hero: comparisunHeroSectionVariants,
        comparisonList: comparisunComparisonListSectionVariants,
        comparisonTopItems: comparisunComparisonTopItemsSectionVariants,
        text: comparisunTextSectionVariants,
        faqs: comparisunFaqsSectionVariants,
        footer: comparisunFooterSectionVariants,
    },
    // Top Compared
    '9tVzCC1soqRMWdzUzrE9': {
        hero: comparisunHeroSectionVariants,
        comparisonList: comparisunComparisonListSectionVariants,
        comparisonTopItems: comparisunComparisonTopItemsSectionVariants,
        text: comparisunTextSectionVariants,
        faqs: comparisunFaqsSectionVariants,
        footer: topComparedFooterSectionVariants,
    },
    // Comparison Test
    'kb1yrghwEBZBhCyLzr1E': {
        hero: comparisunHeroSectionVariants,
        comparisonList: comparisunComparisonListSectionVariants,
        comparisonTopItems: comparisunComparisonTopItemsSectionVariants,
        text: comparisunTextSectionVariants,
        faqs: comparisunFaqsSectionVariants,
        footer: comparisunFooterSectionVariants,
    },
    // LeadsLive
    'RTZtYb5dyMkj1wJ549Hz': {
        hero: leadsLiveHeroSectionVariants,
        comparisonList: leadsLiveComparisonListSectionVariants,
    },
    'Cyder_ai': {
        hero: comparisunHeroSectionVariants,
        comparisonList: comparisunComparisonListSectionVariants,
        comparisonTopItems: comparisunComparisonTopItemsSectionVariants,
        footer: comparisunFooterSectionVariants,
    },
    // Z2A Digital
    'feIW1OiWAk8RQ9hzGeML': {
        hero: z2aHeroSectionVariants,
        comparisonList: z2aComparisonListSectionVariants,
        faqs: z2aFaqsSectionVariants,
        logos: z2aLogosSectionVariants,
        features: z2aFeaturesSectionVariants,
        showcase: z2aShowcaseSectionVariants,
        footer: z2aFooterSectionVariants,
    },
    // Harel
    'ccjBxzBLrRObtOFMVUTr': {
        hero: harelHeroSectionVariants,
    },
    // First Connect
    'jmbSdkB9Kviy18Ew2Zce': {
        hero: firstConnectHeroSectionVariants,
    },
    // Betterspeech
    '1e9MMBdoQsMz5D6jeU7a': {
        hero: betterspeechHeroSectionVariants,
        cta: betterspeechCTASectionVariants,
        showcase: betterspeechShowcaseSectionVariants,
    },
};

/**
 * Types for HTML Stepper Preview events and updates.
 * Mirrors the website-preview-event.ts pattern for stepper forms.
 */
// ============================================================================
// Mapping helpers
// ============================================================================
/**
 * Maps event names to update types.
 * Some events like 'ready' and 'elementSelected' don't create updates.
 */
const stepperEventToUpdateType = {
    htmlElementChanged: 'htmlElementChanged',
    editHtmlAction: 'editHtmlAction',
    editField: 'editField',
    selectImage: 'selectImage',
    selectThumbnail: 'selectThumbnail',
    editImageSettings: 'editImageSettings',
};
/**
 * Update types that should open a modal instead of applying directly
 */
const stepperModalUpdateTypes = new Set([
    'editHtmlAction',
    'editField',
    'selectImage',
    'selectThumbnail',
]);
/**
 * Update types that should be applied directly without a modal
 */
const stepperDirectUpdateTypes = new Set([
    'editImageSettings',
    'htmlElementChanged',
]);

/**
 * Registry of all tracked usage metrics.
 */
const USAGE_METRICS = [
    { type: 'domains', limitKey: 'domains', isConsumable: false },
    { type: 'pages', limitKey: 'pages', isConsumable: false },
    { type: 'publishedPages', limitKey: 'publishedPages', isConsumable: false },
    { type: 'quizzes', limitKey: 'quizzes', isConsumable: false },
    {
        type: 'publishedQuizzes',
        limitKey: 'publishedQuizzes',
        isConsumable: false,
    },
    { type: 'members', limitKey: 'members', isConsumable: false },
    { type: 'aiTokens', limitKey: 'aiCredits', isConsumable: true },
];
/**
 * Limits applied when account has no active subscription and grace period has ended.
 */
const EXPIRED_LIMITS = {
    domains: 0,
    pages: 0,
    publishedPages: 0,
    members: 1, // Owner can still access
    aiCredits: 0,
    buffer: 0,
};

const marketingApproaches = [
    'emotional',
    'rational',
    'storytelling',
    'productFocused',
];
const websiteVariantTestChanges = [
    'hero',
    'heroContent',
    'heroDesign',
    'buttonAnimation',
    'buttonSize',
];

const UserEventTypes = [
    'view',
    'conversion',
    'scroll',
    'button-click',
    'form-submission',
    'ping',
    'scheduled-event',
    'user-error',
];

const websiteLinkTypes = [
    'instagram',
    'facebook',
    'github',
    'twitter',
    'youtube',
    'terms',
    'privacy',
    'linkedin',
    'x',
    'tiktok',
    'custom',
];

export { AnalyticsFilterFields, AnalyticsFilterFieldsMeta, AnalyticsFilterOperators, EXPIRED_LIMITS, EmptyAnalyticsFilters, MultiValueOperators, NoValueOperators, OperatorLabels, OperatorsByFieldType, TextOperators, USAGE_METRICS, UserEventTypes, accountSectionTypeToVariantMapping, bannerSectionVariants, betterspeechCTASectionVariants, betterspeechHeroSectionVariants, betterspeechShowcaseSectionVariants, calendlySectionVariants, categoriesSectionVariants, comparisonListSectionVariants, comparisonTopItemsSectionVariants, comparisunComparisonListSectionVariants, comparisunComparisonTopItemsSectionVariants, comparisunFaqsSectionVariants, comparisunFooterSectionVariants, comparisunHeroSectionVariants, comparisunTextSectionVariants, ctaSectionVariants, customCodeLocations, customCodeSectionVariants, customHTMLSectionVariants, defaultExperimentMetricIds, deviceConstraints, dynamicPageGoals, experimentEndModes, experimentMetricTypes, experimentStatsStatuses, experimentStatuses, faqsSectionVariants, featuresSectionVariants, firstConnectHeroSectionVariants, footerSectionVariants, formFieldTypes, formSectionVariants, harelHeroSectionVariants, headerSectionVariants, heroSectionVariants, htmlStepperFieldTypes, isLegacyCustomCode, isOAuthTokens, leadsLiveComparisonListSectionVariants, leadsLiveHeroSectionVariants, logosSectionVariants, marketingApproaches, preferredRegions, pressMentionsSectionVariants, sectionTypeToVariantMapping, showcaseSectionVariants, stepperDirectUpdateTypes, stepperEventToUpdateType, stepperModalUpdateTypes, testimonialsSectionVariants, textImageSectionVariants, textSectionVariants, topComparedFooterSectionVariants, trustpilotSectionVariants, websiteLinkTypes, websiteVariantTestChanges, z2aComparisonListSectionVariants, z2aFaqsSectionVariants, z2aFeaturesSectionVariants, z2aFooterSectionVariants, z2aHeroSectionVariants, z2aLogosSectionVariants, z2aShowcaseSectionVariants };
//# sourceMappingURL=index.esm.js.map
